import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "./LanguageContext";

const Horse = ({ className, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Premium high-end equestrian horse head */}
    <path d="M3 20c1.5-2 3-5 3.5-8 .5-3-1-5.5-2.5-7 3.5.5 7 2.5 8.5 5.5.8 1.6.8 3.5.5 5.2l2.5 1.8c.8.6 1.5 1.5 1.5 2.5v1c0 1-1 1-2 1H3z" />
    <path d="M11.5 7.5c.5-1.5 1.5-2.5 3-3" />
    <path d="M8.5 10.5c.3-.5.8-.8 1.5-.8" />
  </svg>
);

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();
  const { lang, t, changeLanguage } = useLanguage();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isBelow1200 = windowWidth < 1200;

  const navLinks = [
    { name: t.nav.home, path: lang === "da" ? "/da" : "/en" },
    {
      name: t.nav.horses,
      path: lang === "da" ? "/da/heste" : "/en/horses",
      dropdown: [
        { name: t.nav.horses_sport, path: lang === "da" ? "/da/heste/sportsheste" : "/en/horses/sport-horses" },
        { name: t.nav.horses_sales, path: lang === "da" ? "/da/heste/salg" : "/en/horses/sales" }
      ]
    },
    { name: t.nav.references, path: lang === "da" ? "/da/referencer" : "/en/references" },
    { name: t.nav.sponsors, path: lang === "da" ? "/da/sponsorer" : "/en/sponsors" },
    { name: t.nav.about, path: lang === "da" ? "/da/om-os" : "/en/about-us" },
    { name: t.nav.gallery, path: lang === "da" ? "/da/galleri" : "/en/gallery" },
    { name: t.nav.contact, path: lang === "da" ? "/da/kontakt" : "/en/contact" }
  ];

  const isActive = (path) => {
    if (path === "/da" || path === "/en") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Language Selector Component
  const LanguageSelector = () => (
    <div className="flex items-center gap-1.5 border border-stone-200 bg-stone-50 p-1 rounded-none font-sans-custom">
      <button
        onClick={() => changeLanguage("da")}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-all duration-300 rounded-none cursor-pointer ${
          lang === "da"
            ? "bg-stone-800 text-stone-50 shadow-sm"
            : "text-stone-500 hover:text-stone-900"
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37 28" className="w-3.5 h-2.5 shadow-sm object-cover">
          <rect width="37" height="28" fill="#C8102E"/>
          <rect x="12" width="4" height="28" fill="#FFF"/>
          <rect y="12" width="37" height="4" fill="#FFF"/>
        </svg>
        <span>DA</span>
      </button>
      <button
        onClick={() => changeLanguage("en")}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-all duration-300 rounded-none cursor-pointer ${
          lang === "en"
            ? "bg-stone-800 text-stone-50 shadow-sm"
            : "text-stone-500 hover:text-stone-900"
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 30" className="w-3.5 h-2.5 shadow-sm object-cover">
          <clipPath id="s">
            <path d="M0,0 v30 h50 v-30 z"/>
          </clipPath>
          <path d="M0,0 L50,30 M50,0 L0,30" stroke="#fff" strokeWidth="6"/>
          <path d="M0,0 L50,30 M50,0 L0,30" stroke="#012169" strokeWidth="4"/>
          <path d="M0,15 H50 M25,0 V30" stroke="#fff" strokeWidth="10"/>
          <path d="M0,15 H50 M25,0 V30" stroke="#C8102E" strokeWidth="6"/>
          <path d="M0,0 L50,30 M50,0 L0,30" stroke="#C8102E" strokeWidth="2" clipPath="url(#s)"/>
        </svg>
        <span>EN</span>
      </button>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-stone-200 bg-[#FCF9F5]/90 backdrop-blur-md font-sans-custom">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-12">
        {/* Logo */}
        <Link to={lang === "da" ? "/da" : "/en"} className="flex items-center gap-3 group">
          <div className="relative flex h-20 w-20 items-center justify-center transition-all duration-300 rounded-none">
            <img
              src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/2e87f6a0-71a0-4e77-a6b8-93a81e3adb0a.png"
              alt="Equinesport Heineking Logo"
              className="h-20 w-20 object-contain transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {!isBelow1200 && (
          <nav className="flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.dropdown) {
                const isDropdownActive = isActive(lang === "da" ? "/da/heste" : "/en/horses");
                return (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 hover:text-stone-900 font-sans-custom ${
                        isDropdownActive ? "text-stone-900" : "text-stone-500"
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute left-0 top-full pt-4 w-48">
                        <div className="border border-stone-200 bg-white p-2 shadow-sm rounded-none">
                          {link.dropdown.map((subLink) => (
                            <Link
                              key={subLink.name}
                              to={subLink.path}
                              className={`block px-4 py-2.5 text-[10px] uppercase tracking-widest transition-colors duration-300 hover:bg-stone-50 hover:text-stone-900 rounded-none font-sans-custom ${
                                location.pathname === subLink.path ? "text-stone-900 bg-stone-50 font-semibold" : "text-stone-500"
                              }`}
                            >
                              {subLink.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 hover:text-stone-900 py-1 font-sans-custom ${
                    isActive(link.path) ? "text-stone-900" : "text-stone-500"
                  }`}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className="absolute bottom-0 left-0 h-[1.5px] w-full bg-stone-800" />
                  )}
                </Link>
              );
            })}

            {/* Language Selector Desktop */}
            <div className="ml-4">
              <LanguageSelector />
            </div>
          </nav>
        )}

        {/* Burger Button (< 1200px) */}
        {isBelow1200 && (
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors duration-300 rounded-none"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {isBelow1200 && isOpen && (
        <div className="border-t border-stone-200 bg-[#FCF9F5] px-6 py-6 shadow-sm">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => {
              if (link.dropdown) {
                return (
                  <div key={link.name} className="flex flex-col gap-2">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-stone-400 px-2 font-sans-custom">
                      {link.name}
                    </div>
                    <div className="flex flex-col gap-1 pl-4 border-l border-stone-200">
                      {link.dropdown.map((subLink) => (
                        <Link
                          key={subLink.name}
                          to={subLink.path}
                          onClick={() => setIsOpen(false)}
                          className={`block px-3 py-2 text-[11px] uppercase tracking-widest transition-colors duration-300 hover:text-stone-900 rounded-none font-sans-custom ${
                            location.pathname === subLink.path ? "text-stone-900 bg-stone-100 font-semibold" : "text-stone-500"
                          }`}
                        >
                          {subLink.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 text-[11px] font-medium uppercase tracking-widest transition-colors duration-300 hover:text-stone-900 rounded-none font-sans-custom ${
                    isActive(link.path) ? "text-stone-900 bg-stone-100 font-semibold" : "text-stone-500"
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
